import { useEffect, useRef } from "react";
import TournamentMessageCard from "../TournamentMessageCard";
import { Message } from "pages/MatchChat";
import { useTypedSelector } from "App/Store";

interface MessageChatProps {
  className?: string;
  id?: string;
  messages: Message[];
  maxHeight?: string | number;
  setLimit?: (limit: any) => void;
  hasMore?: boolean;
  loadMoreMessages: () => void;
  isHeight?: boolean;
  isLoading: boolean;
  isGroupChat?: boolean;
  handleViewImage: (image: string) => void;
  isDropDown: {
    open: boolean;
    messageId: string;
  };
  openDropdownModal: (messageId: string) => void;
  closeDropdownModal: () => void;
  openDeleteModal: (messageId: string) => void;
  teamNameByUserId?: Record<string, string> 
}

function groupMessagesByDate(messages: Message[]) {
  return messages?.reduce<Record<string, Message[]>>((acc, msg) => {
    if (!acc[msg.date]) acc[msg.date] = [];
    acc[msg.date].push(msg);
    return acc;
  }, {});
}

function groupBySender(messages: Message[]) {
  const grouped: {
    sender: "me" | "other";
    profileImage?: string;
    name?: string;
    role?: string;
    messages: Message[];
  }[] = [];
  let currentGroup: (typeof grouped)[number] | null = null;
  for (const msg of messages) {
    if (
      !currentGroup ||
      currentGroup.sender !== msg.sender ||
      currentGroup.profileImage !== msg.profileImage
    ) {
      currentGroup = {
        sender: msg.sender,
        profileImage: msg.profileImage,
        name: msg?.name,
        role: msg?.role,
        messages: [msg],
      };
      grouped.push(currentGroup);
    } else {
      currentGroup.messages.push(msg);
    }
  }

  return grouped;
}

function formatDateLabel(dateString: string) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function TournamentChatMessage({
  id = "space",
  messages,
  hasMore,
  teamIdToClass,
  isDropDown,
  isGroupChat = false,
  openDropdownModal,
  closeDropdownModal,
  loadMoreMessages,
  openDeleteModal,
  handleViewImage,
  teamNameByUserId,
}: MessageChatProps & { teamIdToClass: Record<string, string> }) {
  const groupedMessages = groupMessagesByDate(messages);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(messages.length);
  const isInitialScrollDone = useRef(false);
  const preventScrollRef = useRef(false);
  const { user } = useTypedSelector((state) => state.userDetails);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container || messages.length === 0) return;

    const newMessageCount = messages.length;
    const addedMessages = newMessageCount > prevMessageCount.current;

    if (!isInitialScrollDone.current) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      isInitialScrollDone.current = true;
    } else if (addedMessages && !preventScrollRef.current) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }

    prevMessageCount.current = newMessageCount;
    preventScrollRef.current = false;
  }, [messages]);

  return (
    <div ref={messageContainerRef} className={"tournament-message-wrapper"}>
      {hasMore && (
        <div
          className={"load-more"}
          onClick={() => {
            preventScrollRef.current = true;
            loadMoreMessages();
          }}
        >
          <h6 className={"load-more-msg"}>View Older Messages</h6>
        </div>
      )}

      {Object.entries(groupedMessages)
        .sort(
          ([dateA], [dateB]) =>
            new Date(dateA).getTime() - new Date(dateB).getTime()
        )
        .map(([date, msgs]) => {
          const sortedMsgs = [...msgs].sort(
            (a, b) =>
              new Date(a.createdAt || "").getTime() -
              new Date(b.createdAt || "").getTime()
          );
          const senderGroups = groupBySender(sortedMsgs);

          return (
            <div key={date} className={"date-group"}>
              <div className={"day_container"}>
                <div className={"day"}>
                  <h6 className={"text_day"}>{formatDateLabel(date)}</h6>
                </div>
              </div>{" "}
              {senderGroups.map((group, i) => (
                <div key={i} className={"sender-group"}>
                  {group.messages.map((msg) => {
                    const teamClass =
                      msg?.metadata?.teamId &&
                      teamIdToClass[msg.metadata.teamId]
                        ? teamIdToClass[msg.metadata.teamId]
                        : "";
                    return (
                      <div
                        key={msg.id}
                        className={
                          msg.sender === "me" ||
                          user?.id?.toString() === msg?.senderId.toString()
                            ? "user-msgs"
                            : "other-msgs"
                        }
                      >
                        <TournamentMessageCard
                          key={msg.id}
                          message={msg}
                          teamClass={teamClass}
                          handleViewImage={handleViewImage}
                          isDropDown={isDropDown}
                          openDropdownModal={openDropdownModal}
                          closeDropdownModal={closeDropdownModal}
                          openDeleteModal={openDeleteModal}
                          isGroupChat={isGroupChat}
                          teamNameByUserId={teamNameByUserId}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}

      <div id="message-bottom-anchor" />
    </div>
  );
}
